import dayjs from "dayjs"
import { normalizePathname } from "@utils/helpers"
import { MAX_UPLOAD_SIZE_MB, REACT_WEB_ASSETS } from "@constants"
import { FormattedValidationError, RouteTreeNode, Rule, ValidationError } from "@/types"

/**
 * getPathImage
 * @param url
 * @returns {string}
 */
export const getPathImage = (url: string) => {
  return REACT_WEB_ASSETS.PATH_IMAGE + "/" + url
}

type RouteObjectWithSelectKey = RouteTreeNode & {
  selectKey: string
}

export const flattenRoutesTreeAndAddSelectKey = (routeTree: RouteTreeNode) => {
  let result: RouteObjectWithSelectKey[] = []

  function recurseRoutesTree(route: RouteTreeNode, selectKey = "") {
    const current = route as RouteObjectWithSelectKey
    const pathname = normalizePathname(current.path)

    if (current?.label) {
      current.selectKey = pathname
    } else {
      current.selectKey = selectKey
    }

    result.push(current)

    if (current?.children) {
      current.children.forEach((item) => recurseRoutesTree(item, current?.selectKey))
    }
  }

  recurseRoutesTree(routeTree, "")

  return result
}

export const camelToSnake = (str: string | null | undefined) => {
  if (!str) return null

  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

export function snakeToCamel(str: string | null | undefined) {
  if (!str) return null

  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * padStart
 * @param item
 * @param {number} [ndigits=6]
 * @returns {string}
 */
export const padStart = (item: unknown, ndigits?: number) => {
  const DEFAULT_CODE_LENGTH = 6
  let digits = ndigits || DEFAULT_CODE_LENGTH

  const padString = typeof item === "string" ? item : String(item)

  return padString.padStart(digits, "0")
}

export const formatErrorsFromResponse = (errors: ValidationError[]): FormattedValidationError[] => {
  return errors.map((error) => {
    const field = String(error.field)

    const convertedStr = field.replace(/\./g, "_")
    let message = error.message || ""

    if (isClient()) {
      const label = document?.querySelector(`label[for="${convertedStr}"]`)?.getAttribute("title") || ""

      message = error.message.replaceAll("{fieldName}", label)
    }

    const name = field.split(".").map((item) => {
      return isNaN(Number(item)) ? item : Number(item)
    })

    return {
      name,
      errors: [message],
    }
  })
}

export const getFirstErrorFromResponse = (errors: ValidationError[]): string | null => {
  if (!errors || !Array.isArray(errors)) return ""

  const listError = formatErrorsFromResponse(errors)

  return getFirstError(listError)
}

export const getFirstError = (errors: FormattedValidationError[]) => {
  if (!Array.isArray(errors) || !errors) {
    return null
  }

  return errors.map((item) => (item.errors ? item.errors[0] : ""))[0]
}

export const isClient = () => {
  return typeof window !== "undefined"
}

export function deepTrim<T>(obj: unknown, seen = new WeakSet()): T {
  if (typeof obj === "string") {
    return obj.trim() as T
  }

  if (obj === null || obj === undefined || typeof obj !== "object") {
    return obj as T
  }

  if (obj instanceof File || obj instanceof dayjs || obj instanceof Date) {
    return obj as T
  }

  if (seen.has(obj)) {
    throw new Error("Circular reference detected")
  }

  seen.add(obj)

  if (Array.isArray(obj)) {
    return obj.map((item) => deepTrim(item, seen)) as T
  }

  const deepTrimmedEntries = Object.entries(obj).map(([key, value]) => [key, deepTrim(value, seen)])

  return Object.fromEntries(deepTrimmedEntries)
}

export function parseSearchParamToObject(searchParams: URLSearchParams) {
  if (!(searchParams instanceof URLSearchParams)) {
    return {}
  }

  return Object.fromEntries(searchParams.entries().filter(([_, value]) => value))
}

export function removeEmptyValues(values?: object | null) {
  if (!values) return {}

  return Object.fromEntries(Object.entries(values).filter(([, value]) => (value ?? null) !== null))
}

export function deepCopy<T>(obj: T): T {
  if (obj === null || typeof obj !== "object") {
    return obj
  }

  if (dayjs.isDayjs(obj)) {
    return obj.clone() as T
  }

  if (obj instanceof File) {
    return obj
  }

  if (obj instanceof Date) {
    return new Date(obj) as T
  }

  if (obj instanceof RegExp) {
    return new RegExp(obj) as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deepCopy(item)) as T
  }

  const seen = new WeakMap()

  function deepCopyWithCircularCheck<Item>(item: Item): Item {
    if (item === null || typeof item !== "object") {
      return item
    }

    if (dayjs.isDayjs(item)) {
      return item.clone() as Item
    }

    if (item instanceof File) {
      return item
    }

    if (item instanceof Date) {
      return new Date(item) as Item
    }

    if (item instanceof RegExp) {
      return new RegExp(item) as Item
    }

    if (seen.has(item)) {
      return seen.get(item)
    }

    if (Array.isArray(item)) {
      const copy: unknown[] = []

      seen.set(item, copy)

      for (let i = 0; i < item.length; i++) {
        copy[i] = deepCopyWithCircularCheck(item[i])
      }

      return copy as Item
    }

    const itemAsObject = item as Record<string, unknown>
    const copy: typeof itemAsObject = {}

    seen.set(item, copy)

    Object.keys(item).forEach((key) => {
      copy[key] = deepCopyWithCircularCheck(itemAsObject[key])
    })

    return copy as Item
  }

  return deepCopyWithCircularCheck(obj)
}

export function getErrorsMessageWithRules(rules: Rule[], value: string): string[] {
  return rules.reduce<string[]>((acc, { pattern, message }) => {
    if (!pattern?.test(value)) {
      acc.push(message)
    }

    return acc
  }, [])
}

export function extractDigits(str: string | null | undefined) {
  if (!str) return ""

  return str.replace(/\D/g, "") || ""
}

export function formatCurrency(number: number | string | null | undefined) {
  if (!number) return ""

  let parts = number.toString().split(".")

  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")

  return parts.join(".")
}

export function toHalfWidthNumberString(str: string) {
  return [...str]
    .map((char) => {
      const code = char.codePointAt(0)

      // Range for full-width ０ (0xFF10) to ９ (0xFF19)
      if (code && code >= 0xff10 && code <= 0xff19) {
        char = String.fromCodePoint(code - 0xfee0)
      }

      return char
    })
    .join("")
}

export function getMaxMBFromMIMEType(mime: string) {
  const [media, type] = mime.split("/")

  let size = typeof MAX_UPLOAD_SIZE_MB[media] === "object" ? MAX_UPLOAD_SIZE_MB[media][type] : MAX_UPLOAD_SIZE_MB[media]

  if (!size) return 0

  return size
}

export function transformMIMEListToObject(list: string[]) {
  return list.reduce<Record<string, string[]>>((acc, curr) => {
    let [media, type] = curr.split("/")

    if (type === "quicktime") {
      type = "mov"
    }

    acc[media] ??= []
    acc[media].push(`.${type}`)

    return acc
  }, {})
}

export function isSignagePage() {
  return window.location.pathname.startsWith("/signage")
}

export function isDigitOnlyString(str: string | undefined) {
  return str && /^\d+$/.test(str)
}
